import * as THREE from 'three';
import { ZombieConfig, ZombieState, BossVisual, BossConfig, BossWeakPoint } from '../types';

export interface BossAbilityEffect {
  type: string;
  damage?: number;
  // For summon/spawnMinions: spawn new zombies
  spawnCount?: number;
  // For charge/teleport: position change
  targetPosition?: THREE.Vector3;
  // For buff/debuff
  speedMultiplier?: number;
  // For shield
  shieldActive?: boolean;
  // For area effects (poisonCloud, shockwave, groundSlam)
  radius?: number;
  position?: THREE.Vector3;
  // For invisibility
  invisible?: boolean;
}

export class Zombie {
  config: ZombieConfig;
  hp: number;
  maxHp: number;
  state: ZombieState = ZombieState.WANDER;
  mesh: THREE.Group;
  position: THREE.Vector3;
  isBoss: boolean;
  private bossVisual: BossVisual | null = null;
  private bossAbilities: string[] = [];
  bossWeakPoint: BossWeakPoint | null = null;
  private weakPointMarkerMat: THREE.MeshStandardMaterial | null = null;
  private weakPointRingMat: THREE.MeshBasicMaterial | null = null;

  private wanderTarget: THREE.Vector3 | null = null;
  private wanderTimer = 0;
  attackCooldown = 0;
  private alertTimer = 0;

  // Boss ability system
  private abilityCooldown = 0;
  private bossAbilityCooldownTime = 7;
  private abilityIndex = 0;
  pendingAbilityEffects: BossAbilityEffect[] = [];
  shieldActive = false;
  private shieldTimer = 0;
  isInvisible = false;
  private invisibleTimer = 0;
  private chargeTarget: THREE.Vector3 | null = null;
  private chargeSpeed = 0;

  // Damage flash
  private flashTimer = 0;
  private bodyMaterial: THREE.MeshStandardMaterial;

  // Animation references
  private leftArmPivot: THREE.Group | null = null;
  private rightArmPivot: THREE.Group | null = null;
  private leftLegPivot: THREE.Group | null = null;
  private rightLegPivot: THREE.Group | null = null;
  private leftElbow: THREE.Group | null = null;
  private rightElbow: THREE.Group | null = null;
  private leftKnee: THREE.Group | null = null;
  private rightKnee: THREE.Group | null = null;
  private headGroup: THREE.Group | null = null;
  private bodyRoot: THREE.Group | null = null;
  private animPhase: number = Math.random() * Math.PI * 2; // random start phase
  private animSpeed: number = 1.0;
  private baseArmAngle: number = -0.4;
  private baseHunch: number = 0.15;

  // Colors per zombie type
  private static COLORS: Record<string, number> = {
    normal: 0x556b2f,
    tank: 0x4a4a4a,
    spitter: 0x2e8b57,
    exploder: 0xb22222,
    crawler: 0x8b8b5a,
  };

  constructor(config: ZombieConfig, position: THREE.Vector3, hpMult = 1, dmgMult = 1, spdMult = 1, isBoss = false, bossVisual?: BossVisual) {
    this.config = { ...config };
    this.config.hp = Math.round(config.hp * hpMult);
    this.config.damage = Math.round(config.damage * dmgMult);
    this.config.speed = config.speed * spdMult;
    this.hp = this.config.hp;
    this.maxHp = this.config.hp;
    this.position = position.clone();
    this.isBoss = isBoss;
    this.bossVisual = bossVisual ?? null;
    if (isBoss && 'abilities' in config) {
      const bc = config as BossConfig;
      this.bossAbilities = bc.abilities;
      this.bossWeakPoint = bc.weakPoint ?? null;
      this.bossAbilityCooldownTime = bc.abilityCooldown ?? 7;
      this.abilityCooldown = this.bossAbilityCooldownTime;
    }

    this.mesh = new THREE.Group();
    const baseColor = (isBoss && bossVisual) ? bossVisual.bodyColor : (Zombie.COLORS[config.id] ?? 0x556b2f);
    this.bodyMaterial = new THREE.MeshStandardMaterial({
      color: baseColor,
      roughness: 0.8,
    });
    this.createModel();
    this.mesh.position.copy(this.position);
  }

  private createModel(): void {
    const id = this.config.id;
    const bv = this.bossVisual;
    const bossScale = this.isBoss ? 1.5 * (bv?.scale ?? 1.0) : 1.0;
    const typeScale = id === 'tank' ? 1.3 : (id === 'crawler' ? 0.75 : 1.0);
    const s = bossScale * typeScale;

    // --- Type-specific proportions ---
    let torsoWidthTop = 0.38;
    let torsoWidthBot = 0.30;
    let torsoHeight = 0.50;
    let waistHeight = 0.30;
    let waistWidth = 0.26;
    let hipWidth = 0.30;
    let neckHeight = 0.10;
    let headRadius = 0.16;
    let upperArmLen = 0.30;
    let lowerArmLen = 0.28;
    let armThickness = 0.07;
    let upperLegLen = 0.35;
    let lowerLegLen = 0.32;
    let legThickness = 0.09;
    let shoulderWidth = 0.46;
    let hunchAngle = 0.15; // forward lean
    let armRestAngle = -0.4; // arms reaching forward
    let bodyColorHex = Zombie.COLORS[id] ?? 0x556b2f;
    let skinColorHex = 0x7a8b5a;
    let torsoDepth = 0.22;

    if (id === 'tank') {
      torsoWidthTop = 0.52;
      torsoWidthBot = 0.44;
      torsoHeight = 0.60;
      waistHeight = 0.35;
      waistWidth = 0.40;
      hipWidth = 0.42;
      neckHeight = 0.06;
      headRadius = 0.15;
      upperArmLen = 0.35;
      lowerArmLen = 0.32;
      armThickness = 0.11;
      upperLegLen = 0.38;
      lowerLegLen = 0.34;
      legThickness = 0.13;
      shoulderWidth = 0.60;
      hunchAngle = 0.25;
      torsoDepth = 0.28;
      skinColorHex = 0x5a5a5a;
    } else if (id === 'spitter') {
      torsoWidthTop = 0.36;
      torsoWidthBot = 0.42;
      torsoHeight = 0.55;
      waistWidth = 0.38;
      neckHeight = 0.18;
      headRadius = 0.14;
      torsoDepth = 0.30;
      skinColorHex = 0x3a7a4a;
    } else if (id === 'exploder') {
      torsoWidthTop = 0.48;
      torsoWidthBot = 0.50;
      torsoHeight = 0.55;
      waistWidth = 0.46;
      hipWidth = 0.40;
      torsoDepth = 0.34;
      skinColorHex = 0xc44040;
    } else if (id === 'crawler') {
      hunchAngle = 1.2;
      armRestAngle = -1.0;
      skinColorHex = 0x8b8b6a;
    } else {
      // normal
      skinColorHex = 0x6b7a50;
    }

    // Boss visual overrides
    if (this.isBoss && bv) {
      skinColorHex = bv.skinColor;
      bodyColorHex = bv.bodyColor;
      hunchAngle = bv.hunchAngle;
      torsoWidthTop *= bv.torsoWidthMult;
      torsoWidthBot *= bv.torsoWidthMult;
      waistWidth *= bv.torsoWidthMult;
      hipWidth *= bv.torsoWidthMult;
      shoulderWidth *= bv.torsoWidthMult;
      headRadius *= bv.headSizeMult;
    }

    // --- Materials ---
    const skinMat = new THREE.MeshStandardMaterial({
      color: skinColorHex,
      roughness: 0.85,
      metalness: 0.05,
    });
    // Reuse the body material for clothing parts
    this.bodyMaterial.roughness = 0.8;
    this.bodyMaterial.metalness = 0.05;
    const clothMat = this.bodyMaterial;

    // Darker shade for torn rags/details
    const darkClothColor = new THREE.Color(bodyColorHex).multiplyScalar(0.6);
    const ragMat = new THREE.MeshStandardMaterial({
      color: darkClothColor,
      roughness: 0.9,
      metalness: 0.0,
    });

    // --- Clothing materials per zombie type ---
    // Each zombie type has a different outfit
    let shirtColor = 0x4a5a3a;  // normal: dirty lab coat green
    let pantsColor = 0x3a3a3a;  // dark pants
    let hasLabCoat = false;
    let hasTie = false;
    let hasVest = false;
    let hasBelt = true;
    let hasCollar = false;
    let hasBoots = false;
    let hasGloves = false;
    let hasMask = false;

    // Random variation seed per zombie
    const rng = () => Math.random();
    const colorShift = (base: number, range: number) => {
      const c = new THREE.Color(base);
      const h = { h: 0, s: 0, l: 0 };
      c.getHSL(h);
      h.h += (rng() - 0.5) * range;
      h.s *= 0.7 + rng() * 0.6;
      h.l *= 0.8 + rng() * 0.4;
      c.setHSL(h.h, h.s, h.l);
      return c.getHex();
    };

    if (id === 'normal') {
      // Randomized researcher/office worker outfits
      const normalOutfit = Math.floor(rng() * 4);
      if (normalOutfit === 0) {
        // Lab coat
        shirtColor = colorShift(0xccccbb, 0.02);
        pantsColor = colorShift(0x2a2a33, 0.05);
        hasLabCoat = true; hasTie = true; hasCollar = true;
      } else if (normalOutfit === 1) {
        // Business shirt
        shirtColor = colorShift(0x8888aa, 0.1);
        pantsColor = colorShift(0x333333, 0.05);
        hasTie = rng() > 0.5; hasCollar = true;
      } else if (normalOutfit === 2) {
        // Polo shirt
        shirtColor = colorShift(0x446688, 0.15);
        pantsColor = colorShift(0x443322, 0.08);
        hasCollar = true;
      } else {
        // Scrubs (medical)
        shirtColor = colorShift(0x448888, 0.1);
        pantsColor = colorShift(0x448888, 0.1);
      }
    } else if (id === 'tank') {
      const tankOutfit = Math.floor(rng() * 2);
      if (tankOutfit === 0) {
        shirtColor = colorShift(0x3a4a2a, 0.08); pantsColor = shirtColor;
        hasVest = true;
      } else {
        shirtColor = colorShift(0x2a2a3a, 0.06); pantsColor = shirtColor;
        hasVest = true;
      }
      hasBelt = true; hasBoots = true;
    } else if (id === 'spitter') {
      shirtColor = colorShift(0xcccc44, 0.05);
      pantsColor = shirtColor;
      hasMask = true; hasGloves = true;
    } else if (id === 'exploder') {
      const expOutfit = Math.floor(rng() * 2);
      if (expOutfit === 0) {
        shirtColor = colorShift(0xcc6600, 0.08); pantsColor = shirtColor; // orange jumpsuit
      } else {
        shirtColor = colorShift(0xcc2200, 0.08); pantsColor = colorShift(0x333333, 0.05); // red shirt
      }
      hasBelt = true;
    } else if (id === 'crawler') {
      const crawlOutfit = Math.floor(rng() * 3);
      if (crawlOutfit === 0) {
        shirtColor = colorShift(0x4a4a6a, 0.12); pantsColor = colorShift(0x334488, 0.08);
      } else if (crawlOutfit === 1) {
        shirtColor = colorShift(0x666644, 0.1); pantsColor = colorShift(0x3a3a2a, 0.08);
      } else {
        shirtColor = colorShift(0x884444, 0.1); pantsColor = colorShift(0x222222, 0.05);
      }
    }

    const shirtMat = new THREE.MeshStandardMaterial({
      color: shirtColor, roughness: 0.9, metalness: 0.0,
    });
    const pantsMat = new THREE.MeshStandardMaterial({
      color: pantsColor, roughness: 0.85, metalness: 0.0,
    });
    const leatherMat = new THREE.MeshStandardMaterial({
      color: 0x2a1a0a, roughness: 0.6, metalness: 0.15,
    });
    const metalBuckleMat = new THREE.MeshStandardMaterial({
      color: 0x888888, roughness: 0.3, metalness: 0.8,
    });

    // For exploder glow
    const exploderGlowMat = id === 'exploder' ? new THREE.MeshStandardMaterial({
      color: 0xff2200,
      emissive: 0xff2200,
      emissiveIntensity: 0.6,
      roughness: 0.5,
      metalness: 0.1,
      transparent: true,
      opacity: 0.7,
    }) : null;

    // --- Root pivot for hunching ---
    const bodyRoot = new THREE.Group();
    bodyRoot.rotation.x = hunchAngle;
    this.mesh.add(bodyRoot);
    this.bodyRoot = bodyRoot;
    this.baseHunch = hunchAngle;
    this.baseArmAngle = armRestAngle;

    // Animation speed varies by type
    if (id === 'tank') this.animSpeed = 0.5;
    else if (id === 'crawler') this.animSpeed = 1.8;
    else if (id === 'exploder') this.animSpeed = 1.2;
    else if (id === 'spitter') this.animSpeed = 0.9;
    else this.animSpeed = 1.0;

    // Calculate total standing height for positioning
    const totalHeight = (upperLegLen + lowerLegLen + waistHeight + torsoHeight + neckHeight + headRadius * 2) * s;
    const legBaseY = 0;
    const kneeY = legBaseY + lowerLegLen * s;
    const hipY = kneeY + upperLegLen * s;
    const waistTopY = hipY + waistHeight * s;
    const chestTopY = waistTopY + torsoHeight * s;
    const neckTopY = chestTopY + neckHeight * s;
    const headCenterY = neckTopY + headRadius * s;

    // Shift bodyRoot so feet are near ground
    // Crawler: lower the body since it's hunched forward (nearly horizontal)
    if (id === 'crawler') {
      bodyRoot.position.y = hipY * 0.4; // much lower to ground
    } else {
      bodyRoot.position.y = hipY;
    }

    // =====================
    // TORSO (chest region)
    // =====================
    // Chest - tapered box narrower at bottom
    const chestGeo = new THREE.BoxGeometry(torsoWidthTop * s, torsoHeight * s, torsoDepth * s);
    // Taper the bottom vertices inward
    const chestPos = chestGeo.attributes.position;
    for (let i = 0; i < chestPos.count; i++) {
      const y = chestPos.getY(i);
      if (y < 0) {
        const xVal = chestPos.getX(i);
        const zVal = chestPos.getZ(i);
        chestPos.setX(i, xVal * (torsoWidthBot / torsoWidthTop));
        chestPos.setZ(i, zVal * 0.85);
      }
    }
    chestGeo.computeVertexNormals();
    const chest = new THREE.Mesh(chestGeo, clothMat);
    chest.position.y = waistHeight * s + torsoHeight * s * 0.5;
    chest.castShadow = true;
    chest.receiveShadow = true;
    bodyRoot.add(chest);

    // Torn cloth strips hanging from torso
    for (let i = 0; i < 4; i++) {
      const stripW = (0.06 + Math.random() * 0.06) * s;
      const stripH = (0.10 + Math.random() * 0.12) * s;
      const stripGeo = new THREE.PlaneGeometry(stripW, stripH);
      const strip = new THREE.Mesh(stripGeo, ragMat);
      const angle = (i / 4) * Math.PI * 2 + Math.random() * 0.3;
      const radius = torsoWidthBot * 0.5 * s;
      strip.position.set(
        Math.sin(angle) * radius,
        waistHeight * s - stripH * 0.3,
        Math.cos(angle) * radius
      );
      strip.rotation.y = angle;
      strip.rotation.x = Math.random() * 0.3;
      bodyRoot.add(strip);
    }

    // Exploder: glowing belly bulge
    if (id === 'exploder' && exploderGlowMat) {
      const bulge = new THREE.Mesh(
        new THREE.SphereGeometry(torsoWidthBot * 0.55 * s, 10, 8),
        exploderGlowMat
      );
      bulge.position.y = waistHeight * s + torsoHeight * s * 0.3;
      bulge.scale.set(1, 0.9, 1.2);
      bodyRoot.add(bulge);
    }

    // Spitter: bloated belly
    if (id === 'spitter') {
      const belly = new THREE.Mesh(
        new THREE.SphereGeometry(waistWidth * 0.6 * s, 10, 8),
        clothMat
      );
      belly.position.y = waistHeight * s + torsoHeight * s * 0.2;
      belly.position.z = torsoDepth * 0.15 * s;
      belly.scale.set(1, 0.85, 1.3);
      bodyRoot.add(belly);
    }

    // =====================
    // WAIST / PELVIS
    // =====================
    const waistGeo = new THREE.BoxGeometry(waistWidth * s, waistHeight * s, torsoDepth * 0.9 * s);
    const waist = new THREE.Mesh(waistGeo, clothMat);
    waist.position.y = waistHeight * s * 0.5;
    waist.castShadow = true;
    bodyRoot.add(waist);

    // =====================
    // ROTTING SKIN PATCHES on torso
    // =====================
    const rotMat = new THREE.MeshStandardMaterial({
      color: 0x3a4a2a, roughness: 0.95, metalness: 0.0,
      transparent: true, opacity: 0.7,
    });
    const darkRotMat = new THREE.MeshStandardMaterial({
      color: 0x2a2a1a, roughness: 0.95, transparent: true, opacity: 0.6,
    });
    const patchCount = 3 + Math.floor(Math.random() * 4);
    for (let pi = 0; pi < patchCount; pi++) {
      const pSize = (0.03 + Math.random() * 0.05) * s;
      const patchGeo = new THREE.CircleGeometry(pSize, 5 + Math.floor(Math.random() * 3));
      const patch = new THREE.Mesh(patchGeo, Math.random() > 0.5 ? rotMat : darkRotMat);
      const pAngle = Math.random() * Math.PI * 2;
      const pY = waistHeight * s + Math.random() * (torsoHeight * s + waistHeight * s * 0.5);
      const pR = Math.max(torsoWidthTop, waistWidth) * 0.52 * s;
      patch.position.set(Math.sin(pAngle) * pR, pY, Math.cos(pAngle) * pR);
      patch.lookAt(patch.position.clone().add(new THREE.Vector3(Math.sin(pAngle), 0, Math.cos(pAngle))));
      bodyRoot.add(patch);
    }

    // =====================
    // ABDOMINAL WOUND (guts visible)
    // =====================
    if (Math.random() > 0.5 && id !== 'exploder') {
      const gutWoundMat = new THREE.MeshStandardMaterial({
        color: 0x6b1a1a, roughness: 0.7, metalness: 0.05,
      });
      const gutMat = new THREE.MeshStandardMaterial({
        color: 0x884455, roughness: 0.6, metalness: 0.1,
        emissive: 0x220000, emissiveIntensity: 0.15,
      });
      // Wound opening
      const gutWoundGeo = new THREE.PlaneGeometry(torsoWidthBot * 0.4 * s, 0.08 * s);
      const gutWound = new THREE.Mesh(gutWoundGeo, gutWoundMat);
      gutWound.position.set(0.03 * s, waistHeight * s + 0.05 * s, -torsoDepth * 0.5 * s);
      bodyRoot.add(gutWound);
      // Intestine loops
      for (let gi = 0; gi < 3; gi++) {
        const gutGeo = new THREE.TorusGeometry(0.02 * s, 0.006 * s, 5, 8);
        const gut = new THREE.Mesh(gutGeo, gutMat);
        gut.position.set(
          (gi - 1) * 0.025 * s,
          waistHeight * s + 0.03 * s,
          -torsoDepth * 0.55 * s - 0.01 * gi * s
        );
        gut.rotation.set(Math.random() * 0.5, Math.random(), 0);
        bodyRoot.add(gut);
      }
    }

    // =====================
    // NECK
    // =====================
    const neckGeo = new THREE.CylinderGeometry(0.06 * s, 0.08 * s, neckHeight * s, 8);
    const neck = new THREE.Mesh(neckGeo, skinMat);
    neck.position.y = waistHeight * s + torsoHeight * s + neckHeight * s * 0.5;
    neck.castShadow = true;
    bodyRoot.add(neck);

    // Neck tendons
    const tendonNeckMat = new THREE.MeshStandardMaterial({
      color: 0x885544, roughness: 0.7, metalness: 0.05,
    });
    for (const tSide of [-1, 0, 1]) {
      const tendonGeo = new THREE.CylinderGeometry(0.006 * s, 0.004 * s, neckHeight * 1.2 * s, 4);
      const tendon = new THREE.Mesh(tendonGeo, tendonNeckMat);
      tendon.position.set(
        tSide * 0.035 * s,
        waistHeight * s + torsoHeight * s + neckHeight * s * 0.5,
        -0.035 * s
      );
      tendon.rotation.z = tSide * 0.15;
      bodyRoot.add(tendon);
    }

    // Throat bulge (Adam's apple)
    const throatGeo = new THREE.SphereGeometry(0.015 * s, 5, 4);
    const throat = new THREE.Mesh(throatGeo, skinMat);
    throat.position.set(0, waistHeight * s + torsoHeight * s + neckHeight * s * 0.4, -0.055 * s);
    bodyRoot.add(throat);

    // =====================
    // HEAD
    // =====================
    const headGroup = new THREE.Group();
    headGroup.position.y = headCenterY - hipY;
    bodyRoot.add(headGroup);
    this.headGroup = headGroup;

    // Cranium
    const craniumGeo = new THREE.SphereGeometry(headRadius * s, 10, 8);
    const cranium = new THREE.Mesh(craniumGeo, skinMat);
    cranium.scale.set(1.0, 1.1, 1.0);
    cranium.castShadow = true;
    headGroup.add(cranium);

    // Jaw (lower part of head)
    const jawGeo = new THREE.BoxGeometry(headRadius * 1.2 * s, headRadius * 0.4 * s, headRadius * 0.9 * s);
    const jaw = new THREE.Mesh(jawGeo, skinMat);
    jaw.position.y = -headRadius * 0.65 * s;
    jaw.position.z = -headRadius * 0.15 * s;
    jaw.rotation.x = 0.1; // slightly open mouth
    headGroup.add(jaw);

    // Brow ridge
    const browGeo = new THREE.BoxGeometry(headRadius * 1.5 * s, headRadius * 0.2 * s, headRadius * 0.4 * s);
    const brow = new THREE.Mesh(browGeo, skinMat);
    brow.position.y = headRadius * 0.3 * s;
    brow.position.z = -headRadius * 0.7 * s;
    headGroup.add(brow);

    // Sunken cheeks (visible indentations on face)
    const cheekMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(skinColorHex).multiplyScalar(0.5),
      roughness: 0.9,
    });
    for (const cSide of [-1, 1]) {
      const cheekGeo = new THREE.SphereGeometry(0.04 * s, 6, 5);
      const cheek = new THREE.Mesh(cheekGeo, cheekMat);
      cheek.position.set(cSide * headRadius * 0.6 * s, -headRadius * 0.25 * s, -(headRadius * 0.7 * s));
      cheek.scale.set(0.8, 1, 0.5);
      headGroup.add(cheek);
    }

    // Cheekbones (prominent, protruding)
    for (const cbSide of [-1, 1]) {
      const cbGeo = new THREE.SphereGeometry(0.025 * s, 5, 4);
      const cb = new THREE.Mesh(cbGeo, skinMat);
      cb.position.set(cbSide * headRadius * 0.7 * s, -0.01 * s, -(headRadius * 0.8 * s));
      cb.scale.set(1.2, 0.6, 0.6);
      headGroup.add(cb);
    }

    // Mouth / darkened lips
    const lipMat = new THREE.MeshStandardMaterial({
      color: 0x3a2a2a, roughness: 0.8,
    });
    const upperLipGeo = new THREE.BoxGeometry(headRadius * 0.6 * s, 0.012 * s, 0.02 * s);
    const upperLip = new THREE.Mesh(upperLipGeo, lipMat);
    upperLip.position.set(0, -headRadius * 0.45 * s, -(headRadius * s + 0.005 * s));
    headGroup.add(upperLip);

    // =====================
    // EYES - positioned well in front of cranium
    // =====================
    const eyeRadius = 0.035 * s;
    const irisColor = this.isBoss ? 0xff0000 : (id === 'exploder' ? 0xff4400 : (id === 'spitter' ? 0x44ff44 : 0xccaa44));
    const glowIntensity = this.isBoss ? 2.5 : (id === 'exploder' ? 2.0 : 1.5);
    const eyeSpacing = 0.06 * s;
    const eyeHeight = 0.02 * s;
    // Push eyes far in front so they protrude from the head
    const eyeDepth = -(headRadius * s + eyeRadius * 0.6);

    for (const eSide of [-1, 1]) {
      const eyeGroup = new THREE.Group();
      eyeGroup.position.set(eSide * eyeSpacing, eyeHeight, eyeDepth);

      // Eye socket (dark ring around eye)
      const socketGeo = new THREE.RingGeometry(eyeRadius * 0.9, eyeRadius * 1.5, 10);
      const socketMat = new THREE.MeshStandardMaterial({ color: 0x1a0a0a, roughness: 0.9, side: THREE.DoubleSide });
      const socket = new THREE.Mesh(socketGeo, socketMat);
      socket.position.z = eyeRadius * 0.3;
      eyeGroup.add(socket);

      // Eyeball - half-sphere facing forward
      const scleraMat = new THREE.MeshStandardMaterial({
        color: 0xddcc88,
        roughness: 0.3,
        emissive: irisColor,
        emissiveIntensity: 0.15,
      });
      const eyeballGeo = new THREE.SphereGeometry(eyeRadius, 12, 10);
      const eyeball = new THREE.Mesh(eyeballGeo, scleraMat);
      eyeGroup.add(eyeball);

      // Bloodshot veins
      const veinEyeMat = new THREE.MeshBasicMaterial({
        color: 0xcc2200, transparent: true, opacity: 0.6,
      });
      for (let vi = 0; vi < 5; vi++) {
        const vAngle = (vi / 5) * Math.PI * 2 + eSide;
        const vLen = eyeRadius * (0.6 + Math.random() * 0.5);
        const vGeo = new THREE.CylinderGeometry(0.001 * s, 0.002 * s, vLen, 3);
        const vMesh = new THREE.Mesh(vGeo, veinEyeMat);
        const vR = eyeRadius * 0.4;
        vMesh.position.set(Math.sin(vAngle) * vR, Math.cos(vAngle) * vR, -eyeRadius * 0.2);
        vMesh.lookAt(new THREE.Vector3(0, 0, -eyeRadius));
        eyeGroup.add(vMesh);
      }

      // Iris - large colored disc
      const irisMat = new THREE.MeshStandardMaterial({
        color: irisColor,
        emissive: irisColor,
        emissiveIntensity: glowIntensity,
        roughness: 0.15,
      });
      const irisGeo = new THREE.CircleGeometry(eyeRadius * 0.55, 16);
      const iris = new THREE.Mesh(irisGeo, irisMat);
      iris.position.z = -eyeRadius * 0.98;
      eyeGroup.add(iris);

      // Pupil
      const pupilGeo = new THREE.CircleGeometry(eyeRadius * 0.28, 10);
      const pupilMat = new THREE.MeshBasicMaterial({ color: 0x000000 });
      const pupil = new THREE.Mesh(pupilGeo, pupilMat);
      pupil.position.z = -eyeRadius * 0.99;
      eyeGroup.add(pupil);

      // Highlight reflection
      const reflGeo = new THREE.CircleGeometry(eyeRadius * 0.1, 6);
      const reflMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
      const refl = new THREE.Mesh(reflGeo, reflMat);
      refl.position.set(eyeRadius * 0.15, eyeRadius * 0.12, -eyeRadius * 1.0);
      eyeGroup.add(refl);

      // Upper eyelid (thin strip above eye, does NOT cover the eye)
      const lidMat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(skinColorHex).multiplyScalar(0.7), roughness: 0.85,
      });
      const lidGeo = new THREE.BoxGeometry(eyeRadius * 2.4, eyeRadius * 0.3, eyeRadius * 0.4);
      const lid = new THREE.Mesh(lidGeo, lidMat);
      lid.position.set(0, eyeRadius * 1.0, -eyeRadius * 0.5);
      eyeGroup.add(lid);

      // Lower lid (thin strip below eye)
      const lowerLidGeo = new THREE.BoxGeometry(eyeRadius * 2.0, eyeRadius * 0.15, eyeRadius * 0.3);
      const lowerLid = new THREE.Mesh(lowerLidGeo, lidMat);
      lowerLid.position.set(0, -eyeRadius * 1.0, -eyeRadius * 0.4);
      eyeGroup.add(lowerLid);

      // Asymmetry - each eye slightly different
      if (eSide === 1) {
        eyeGroup.rotation.y = (Math.random() - 0.5) * 0.2;
        eyeGroup.scale.setScalar(0.9 + Math.random() * 0.2);
      }

      headGroup.add(eyeGroup);
    }

    // --- GORE DETAILS on head ---
    // Ears
    const earMat = skinMat;
    for (const eSide of [-1, 1]) {
      const earGeo = new THREE.SphereGeometry(0.04 * s, 5, 4);
      const ear = new THREE.Mesh(earGeo, earMat);
      ear.position.set(eSide * headRadius * 0.95 * s, 0.0, -headRadius * 0.1 * s);
      ear.scale.set(0.5, 0.8, 0.6);
      headGroup.add(ear);
    }

    // Nose - protruding from face
    const noseGeo = new THREE.BoxGeometry(0.03 * s, 0.05 * s, 0.05 * s);
    const nose = new THREE.Mesh(noseGeo, skinMat);
    nose.position.set(0, -0.03 * s, -(headRadius * s + 0.02 * s));
    headGroup.add(nose);
    // Nostrils
    for (const nSide of [-1, 1]) {
      const nostrilGeo = new THREE.SphereGeometry(0.008 * s, 4, 3);
      const nostrilMat = new THREE.MeshStandardMaterial({ color: 0x1a1a1a });
      const nostril = new THREE.Mesh(nostrilGeo, nostrilMat);
      nostril.position.set(nSide * 0.01 * s, -0.05 * s, -(headRadius * s + 0.04 * s));
      headGroup.add(nostril);
    }

    // Teeth (visible through open jaw)
    const teethMat = new THREE.MeshStandardMaterial({
      color: 0xddcc99,
      roughness: 0.4,
      metalness: 0.1,
    });
    // Upper teeth row - visible in front
    const upperTeethGeo = new THREE.BoxGeometry(headRadius * 0.8 * s, 0.015 * s, 0.03 * s);
    const upperTeeth = new THREE.Mesh(upperTeethGeo, teethMat);
    upperTeeth.position.set(0, -headRadius * 0.5 * s, -(headRadius * s + 0.01 * s));
    headGroup.add(upperTeeth);
    // Individual front teeth
    for (let ti = -2; ti <= 2; ti++) {
      if (Math.random() < 0.25) continue; // missing teeth
      const toothGeo = new THREE.BoxGeometry(0.012 * s, 0.025 * s, 0.015 * s);
      const tooth = new THREE.Mesh(toothGeo, teethMat);
      tooth.position.set(ti * 0.016 * s, -headRadius * 0.55 * s, -(headRadius * s + 0.015 * s));
      headGroup.add(tooth);
    }
    // Lower teeth on jaw
    const lowerTeethGeo = new THREE.BoxGeometry(headRadius * 0.7 * s, 0.012 * s, 0.025 * s);
    const lowerTeeth = new THREE.Mesh(lowerTeethGeo, teethMat);
    lowerTeeth.position.set(0, 0.01 * s, -headRadius * 0.4 * s);
    jaw.add(lowerTeeth);

    // Wound/gash on head (exposed flesh)
    const woundMat = new THREE.MeshStandardMaterial({
      color: 0x8b1a1a,
      roughness: 0.6,
      metalness: 0.1,
    });
    if (Math.random() > 0.3) {
      const gashGeo = new THREE.PlaneGeometry(0.06 * s, 0.03 * s);
      const gash = new THREE.Mesh(gashGeo, woundMat);
      const gashSide = Math.random() > 0.5 ? 1 : -1;
      gash.position.set(gashSide * headRadius * 0.6 * s, headRadius * 0.2 * s, -headRadius * 0.4 * s);
      gash.rotation.y = gashSide * 0.5;
      gash.rotation.z = 0.3;
      headGroup.add(gash);
    }

    // Sparse hair strands
    const hairMat = new THREE.MeshStandardMaterial({ color: 0x222211, roughness: 0.95 });
    const hairCount = 3 + Math.floor(Math.random() * 5);
    for (let hi = 0; hi < hairCount; hi++) {
      const hairGeo = new THREE.CylinderGeometry(0.003 * s, 0.002 * s, (0.06 + Math.random() * 0.08) * s, 3);
      const hair = new THREE.Mesh(hairGeo, hairMat);
      const hAngle = Math.random() * Math.PI * 2;
      const hR = headRadius * (0.7 + Math.random() * 0.25) * s;
      hair.position.set(Math.sin(hAngle) * hR, headRadius * 0.6 * s, Math.cos(hAngle) * hR);
      hair.rotation.x = (Math.random() - 0.5) * 0.8;
      hair.rotation.z = (Math.random() - 0.5) * 0.8;
      headGroup.add(hair);
    }

    // Counter-rotate head to partly undo hunch (zombie looking forward)
    headGroup.rotation.x = -hunchAngle * 0.6;

    // =====================
    // TORSO GORE DETAILS
    // =====================
    // Exposed ribs (on one side)
    const boneMat = new THREE.MeshStandardMaterial({
      color: 0xddd8c4,
      roughness: 0.5,
      metalness: 0.05,
    });
    if (Math.random() > 0.3) {
      const ribSide = Math.random() > 0.5 ? 1 : -1;
      for (let ri = 0; ri < 3; ri++) {
        const ribGeo = new THREE.CylinderGeometry(0.008 * s, 0.006 * s, torsoWidthTop * 0.35 * s, 4);
        ribGeo.rotateZ(Math.PI / 2);
        const rib = new THREE.Mesh(ribGeo, boneMat);
        rib.position.set(
          ribSide * torsoWidthTop * 0.22 * s,
          waistHeight * s + torsoHeight * s * (0.55 + ri * 0.12),
          -torsoDepth * 0.35 * s
        );
        rib.rotation.y = ribSide * 0.3;
        bodyRoot.add(rib);
      }
      // Wound opening where ribs are exposed
      const ribWoundGeo = new THREE.PlaneGeometry(torsoWidthTop * 0.3 * s, torsoHeight * 0.4 * s);
      const ribWound = new THREE.Mesh(ribWoundGeo, woundMat);
      ribWound.position.set(
        ribSide * torsoWidthTop * 0.25 * s,
        waistHeight * s + torsoHeight * s * 0.65,
        -torsoDepth * 0.48 * s
      );
      ribWound.rotation.y = ribSide * 0.4;
      bodyRoot.add(ribWound);
    }

    // Blood stains on torso (dark red patches)
    const bloodMat = new THREE.MeshStandardMaterial({
      color: 0x4a0000,
      roughness: 0.95,
      metalness: 0.0,
      transparent: true,
      opacity: 0.8,
    });
    const bloodCount = 2 + Math.floor(Math.random() * 3);
    for (let bi = 0; bi < bloodCount; bi++) {
      const bSize = (0.04 + Math.random() * 0.06) * s;
      const bloodGeo = new THREE.CircleGeometry(bSize, 6);
      const blood = new THREE.Mesh(bloodGeo, bloodMat);
      const bAngle = Math.random() * Math.PI * 2;
      blood.position.set(
        Math.sin(bAngle) * torsoWidthTop * 0.3 * s,
        waistHeight * s + Math.random() * torsoHeight * s,
        Math.cos(bAngle) * torsoDepth * 0.5 * s - 0.01
      );
      blood.lookAt(blood.position.clone().add(new THREE.Vector3(Math.sin(bAngle), 0, Math.cos(bAngle))));
      bodyRoot.add(blood);
    }

    // Exposed spine bump on back (if hunched)
    if (hunchAngle > 0.1) {
      const spineCount = 3;
      for (let si = 0; si < spineCount; si++) {
        const vertebraGeo = new THREE.SphereGeometry(0.02 * s, 5, 4);
        const vertebra = new THREE.Mesh(vertebraGeo, boneMat);
        vertebra.position.set(
          0,
          waistHeight * s + torsoHeight * s * (0.5 + si * 0.15),
          torsoDepth * 0.45 * s
        );
        vertebra.scale.set(1.2, 0.8, 1);
        bodyRoot.add(vertebra);
      }
    }

    // Shoulder muscle/tendon detail
    const tendonMat = new THREE.MeshStandardMaterial({
      color: 0x994444,
      roughness: 0.7,
      metalness: 0.05,
    });
    for (const sSide of [-1, 1]) {
      // Shoulder pad / deltoid muscle
      const deltoidGeo = new THREE.SphereGeometry(armThickness * 1.4 * s, 6, 5);
      const deltoid = new THREE.Mesh(deltoidGeo, skinMat);
      deltoid.position.set(
        sSide * shoulderWidth * 0.5 * s,
        waistHeight * s + torsoHeight * s * 0.92,
        0
      );
      deltoid.scale.set(1.2, 0.8, 0.9);
      bodyRoot.add(deltoid);
    }

    // =====================
    // CLOTHING
    // =====================

    // SHIRT / LAB COAT - covers torso
    const shirtGeo = new THREE.BoxGeometry(
      torsoWidthTop * 1.08 * s, torsoHeight * 1.02 * s, torsoDepth * 1.08 * s
    );
    // Taper shirt bottom like chest
    const shirtPos = shirtGeo.attributes.position;
    for (let i = 0; i < shirtPos.count; i++) {
      if (shirtPos.getY(i) < 0) {
        shirtPos.setX(i, shirtPos.getX(i) * (torsoWidthBot / torsoWidthTop));
      }
    }
    shirtGeo.computeVertexNormals();
    const shirt = new THREE.Mesh(shirtGeo, shirtMat);
    shirt.position.y = waistHeight * s + torsoHeight * s * 0.5;
    bodyRoot.add(shirt);

    // Collar
    if (hasCollar) {
      for (const cSide of [-1, 1]) {
        const collarGeo = new THREE.BoxGeometry(torsoWidthTop * 0.3 * s, 0.05 * s, torsoDepth * 0.3 * s);
        const collar = new THREE.Mesh(collarGeo, shirtMat);
        collar.position.set(
          cSide * torsoWidthTop * 0.18 * s,
          waistHeight * s + torsoHeight * s + 0.02 * s,
          -torsoDepth * 0.2 * s
        );
        collar.rotation.x = -0.3;
        collar.rotation.z = cSide * 0.4;
        bodyRoot.add(collar);
      }
    }

    // Lab coat extension (below waist)
    if (hasLabCoat) {
      const coatSkirtGeo = new THREE.BoxGeometry(
        waistWidth * 1.2 * s, upperLegLen * 0.6 * s, torsoDepth * 1.1 * s
      );
      // Flare the bottom out
      const coatPos = coatSkirtGeo.attributes.position;
      for (let i = 0; i < coatPos.count; i++) {
        if (coatPos.getY(i) < 0) {
          coatPos.setX(i, coatPos.getX(i) * 1.3);
          coatPos.setZ(i, coatPos.getZ(i) * 1.2);
        }
      }
      coatSkirtGeo.computeVertexNormals();
      const coatSkirt = new THREE.Mesh(coatSkirtGeo, shirtMat);
      coatSkirt.position.y = -upperLegLen * 0.1 * s;
      bodyRoot.add(coatSkirt);

      // Front opening split
      const splitGeo = new THREE.PlaneGeometry(0.01 * s, upperLegLen * 0.6 * s);
      const splitMat = new THREE.MeshStandardMaterial({ color: 0x000000, transparent: true, opacity: 0.3 });
      const split = new THREE.Mesh(splitGeo, splitMat);
      split.position.set(0, -upperLegLen * 0.1 * s, -torsoDepth * 0.56 * s);
      bodyRoot.add(split);

      // Coat pockets
      for (const pSide of [-1, 1]) {
        const pocketGeo = new THREE.BoxGeometry(0.06 * s, 0.05 * s, 0.005 * s);
        const pocket = new THREE.Mesh(pocketGeo, new THREE.MeshStandardMaterial({
          color: new THREE.Color(shirtColor).multiplyScalar(0.85), roughness: 0.9,
        }));
        pocket.position.set(
          pSide * torsoWidthBot * 0.25 * s,
          waistHeight * s + torsoHeight * s * 0.2,
          -torsoDepth * 0.54 * s
        );
        bodyRoot.add(pocket);
      }
    }

    // Tie
    if (hasTie) {
      const tieMat = new THREE.MeshStandardMaterial({ color: 0x882222, roughness: 0.7 });
      // Knot
      const knotGeo = new THREE.SphereGeometry(0.015 * s, 5, 4);
      const knot = new THREE.Mesh(knotGeo, tieMat);
      knot.position.set(0, waistHeight * s + torsoHeight * s * 0.92, -torsoDepth * 0.55 * s);
      bodyRoot.add(knot);
      // Tie body
      const tieGeo = new THREE.BoxGeometry(0.04 * s, torsoHeight * 0.7 * s, 0.005 * s);
      // Taper to point at bottom
      const tiePos = tieGeo.attributes.position;
      for (let i = 0; i < tiePos.count; i++) {
        if (tiePos.getY(i) < 0) {
          tiePos.setX(i, tiePos.getX(i) * 0.4);
        }
      }
      tieGeo.computeVertexNormals();
      const tie = new THREE.Mesh(tieGeo, tieMat);
      tie.position.set(0.005 * s, waistHeight * s + torsoHeight * s * 0.55, -torsoDepth * 0.55 * s);
      tie.rotation.z = 0.1; // slightly askew
      bodyRoot.add(tie);
    }

    // Tactical vest
    if (hasVest) {
      const vestMat = new THREE.MeshStandardMaterial({ color: 0x2a3a1a, roughness: 0.7, metalness: 0.1 });
      // Front vest
      const vestFrontGeo = new THREE.BoxGeometry(torsoWidthTop * 0.9 * s, torsoHeight * 0.7 * s, 0.03 * s);
      const vestFront = new THREE.Mesh(vestFrontGeo, vestMat);
      vestFront.position.set(0, waistHeight * s + torsoHeight * s * 0.6, -torsoDepth * 0.56 * s);
      bodyRoot.add(vestFront);
      // Back vest
      const vestBack = new THREE.Mesh(vestFrontGeo.clone(), vestMat);
      vestBack.position.set(0, waistHeight * s + torsoHeight * s * 0.6, torsoDepth * 0.56 * s);
      bodyRoot.add(vestBack);
      // Shoulder straps
      for (const vSide of [-1, 1]) {
        const strapGeo = new THREE.BoxGeometry(0.04 * s, torsoHeight * 0.3 * s, torsoDepth * 1.1 * s);
        const strap = new THREE.Mesh(strapGeo, vestMat);
        strap.position.set(
          vSide * torsoWidthTop * 0.35 * s,
          waistHeight * s + torsoHeight * s * 0.85,
          0
        );
        bodyRoot.add(strap);
      }
      // Pouches on vest
      for (let pi = 0; pi < 3; pi++) {
        const pouchGeo = new THREE.BoxGeometry(0.035 * s, 0.04 * s, 0.025 * s);
        const pouch = new THREE.Mesh(pouchGeo, new THREE.MeshStandardMaterial({
          color: 0x3a4a2a, roughness: 0.8,
        }));
        pouch.position.set(
          (pi - 1) * 0.06 * s,
          waistHeight * s + torsoHeight * s * 0.35,
          -torsoDepth * 0.58 * s
        );
        bodyRoot.add(pouch);
      }
    }

    // Belt
    if (hasBelt) {
      const beltGeo = new THREE.BoxGeometry(waistWidth * 1.15 * s, 0.03 * s, torsoDepth * 1.05 * s);
      const belt = new THREE.Mesh(beltGeo, leatherMat);
      belt.position.y = waistHeight * s + 0.01 * s;
      bodyRoot.add(belt);
      // Buckle
      const buckleGeo = new THREE.BoxGeometry(0.03 * s, 0.035 * s, 0.01 * s);
      const buckle = new THREE.Mesh(buckleGeo, metalBuckleMat);
      buckle.position.set(0, waistHeight * s + 0.01 * s, -torsoDepth * 0.53 * s);
      bodyRoot.add(buckle);
    }

    // Broken gas mask (spitter)
    if (hasMask) {
      const maskMat = new THREE.MeshStandardMaterial({ color: 0x333333, roughness: 0.5, metalness: 0.3 });
      // Mask body (half broken, hanging to one side)
      const maskGeo = new THREE.CylinderGeometry(headRadius * 0.5 * s, headRadius * 0.6 * s, 0.06 * s, 8);
      const mask = new THREE.Mesh(maskGeo, maskMat);
      mask.position.set(
        0.04 * s,
        headCenterY - hipY - headRadius * 0.4 * s,
        -headRadius * 0.8 * s
      );
      mask.rotation.x = Math.PI / 2;
      mask.rotation.z = 0.4; // tilted
      bodyRoot.add(mask);
      // Filter canister
      const filterGeo = new THREE.CylinderGeometry(0.02 * s, 0.02 * s, 0.04 * s, 6);
      const filter = new THREE.Mesh(filterGeo, maskMat);
      filter.position.set(
        0.07 * s,
        headCenterY - hipY - headRadius * 0.5 * s,
        -headRadius * 0.7 * s
      );
      filter.rotation.x = Math.PI / 2 + 0.3;
      bodyRoot.add(filter);
      // Strap hanging
      const strapGeo = new THREE.CylinderGeometry(0.004 * s, 0.004 * s, headRadius * 1.5 * s, 4);
      const strapMesh = new THREE.Mesh(strapGeo, new THREE.MeshStandardMaterial({ color: 0x222222 }));
      strapMesh.position.set(
        0.06 * s,
        headCenterY - hipY,
        0
      );
      strapMesh.rotation.z = 0.3;
      bodyRoot.add(strapMesh);
    }

    // Gloves (spitter hazmat)
    if (hasGloves) {
      // Will be added to arms below via flag
    }

    // PANTS - cover upper legs (added as oversized cylinders in buildLeg)
    // Handled in the buildLeg function via pantsMat

    // =====================
    // ARMS (upper + lower + hand)
    // =====================
    const buildArm = (side: number) => {
      const armPivot = new THREE.Group();
      armPivot.position.set(side * shoulderWidth * 0.5 * s, waistHeight * s + torsoHeight * s * 0.9, 0);
      armPivot.rotation.x = armRestAngle;
      armPivot.rotation.z = side * 0.1; // slight outward angle

      // Upper arm
      const upperArmGeo = new THREE.CylinderGeometry(armThickness * s, armThickness * 0.85 * s, upperArmLen * s, 6);
      const upperArm = new THREE.Mesh(upperArmGeo, skinMat);
      upperArm.position.y = -upperArmLen * 0.5 * s;
      upperArm.castShadow = true;
      armPivot.add(upperArm);

      // Shirt sleeve (covers upper arm)
      const sleeveLen = upperArmLen * 0.55;
      const sleeveGeo = new THREE.CylinderGeometry(armThickness * 1.25 * s, armThickness * 1.15 * s, sleeveLen * s, 6);
      const sleeve = new THREE.Mesh(sleeveGeo, shirtMat);
      sleeve.position.y = -sleeveLen * 0.4 * s;
      armPivot.add(sleeve);
      // Torn edge of sleeve
      const tornEdgeGeo = new THREE.CylinderGeometry(armThickness * 1.18 * s, armThickness * 1.3 * s, 0.02 * s, 8, 1, true);
      const tornEdge = new THREE.Mesh(tornEdgeGeo, ragMat);
      tornEdge.position.y = -sleeveLen * s;
      armPivot.add(tornEdge);
      // Elbow pivot
      const elbowPivot = new THREE.Group();
      elbowPivot.position.y = -upperArmLen * s;
      elbowPivot.rotation.x = -0.3 - Math.random() * 0.2;
      armPivot.add(elbowPivot);

      // Hazmat gloves
      if (hasGloves) {
        const gloveGeo = new THREE.CylinderGeometry(armThickness * 1.1 * s, armThickness * 0.9 * s, lowerArmLen * 0.7 * s, 6);
        const gloveMat = new THREE.MeshStandardMaterial({ color: 0x888822, roughness: 0.6 });
        const glove = new THREE.Mesh(gloveGeo, gloveMat);
        glove.position.y = -lowerArmLen * 0.4 * s;
        elbowPivot.add(glove);
      }

      // Lower arm
      const lowerArmGeo = new THREE.CylinderGeometry(armThickness * 0.8 * s, armThickness * 0.65 * s, lowerArmLen * s, 6);
      const lowerArm = new THREE.Mesh(lowerArmGeo, skinMat);
      lowerArm.position.y = -lowerArmLen * 0.5 * s;
      lowerArm.castShadow = true;
      elbowPivot.add(lowerArm);

      // Hand (flattened sphere)
      const handGeo = new THREE.SphereGeometry(armThickness * 1.1 * s, 6, 5);
      const hand = new THREE.Mesh(handGeo, skinMat);
      hand.position.y = -lowerArmLen * s;
      hand.scale.set(1, 0.6, 1.2);
      elbowPivot.add(hand);

      // Fingers (four fingers + thumb)
      for (let f = -1.5; f <= 1.5; f++) {
        const fLen = (0.04 + Math.random() * 0.025) * s;
        const fingerGeo = new THREE.CylinderGeometry(0.01 * s, 0.006 * s, fLen, 4);
        const finger = new THREE.Mesh(fingerGeo, skinMat);
        finger.position.set(f * 0.015 * s, -lowerArmLen * s - fLen * 0.4, -0.01 * s);
        finger.rotation.x = -0.4 - Math.random() * 0.3;
        // Some fingers missing or bent wrong
        if (Math.random() < 0.15) continue;
        elbowPivot.add(finger);
      }
      // Thumb
      const thumbGeo = new THREE.CylinderGeometry(0.011 * s, 0.007 * s, 0.04 * s, 4);
      const thumb = new THREE.Mesh(thumbGeo, skinMat);
      thumb.position.set(side * 0.03 * s, -lowerArmLen * s - 0.01 * s, -0.02 * s);
      thumb.rotation.z = side * 0.6;
      thumb.rotation.x = -0.2;
      elbowPivot.add(thumb);

      // Fingernails (dirty/dark)
      const nailMat = new THREE.MeshStandardMaterial({ color: 0x3a3a2a, roughness: 0.5 });
      for (let f = -1; f <= 1; f++) {
        const nailGeo = new THREE.BoxGeometry(0.008 * s, 0.003 * s, 0.01 * s);
        const nail = new THREE.Mesh(nailGeo, nailMat);
        nail.position.set(f * 0.015 * s, -lowerArmLen * s - 0.06 * s, -0.02 * s);
        elbowPivot.add(nail);
      }

      // Wound on arm (random)
      if (Math.random() > 0.5) {
        const armWoundGeo = new THREE.PlaneGeometry(0.04 * s, 0.025 * s);
        const armWoundMat = new THREE.MeshStandardMaterial({
          color: 0x8b1a1a, roughness: 0.6, metalness: 0.1,
        });
        const armWound = new THREE.Mesh(armWoundGeo, armWoundMat);
        armWound.position.set(side * armThickness * 0.5 * s, -upperArmLen * 0.5 * s, 0);
        armWound.rotation.y = side * 0.5;
        armPivot.add(armWound);

        // Exposed bone at wound
        const armBoneGeo = new THREE.CylinderGeometry(0.008 * s, 0.008 * s, 0.05 * s, 4);
        const armBoneMat = new THREE.MeshStandardMaterial({ color: 0xddd8c4, roughness: 0.5 });
        const armBone = new THREE.Mesh(armBoneGeo, armBoneMat);
        armBone.position.copy(armWound.position);
        armBone.position.x += side * 0.01 * s;
        armPivot.add(armBone);
      }

      // Veins (subtle dark lines on forearm)
      const veinMat = new THREE.MeshStandardMaterial({
        color: 0x334433, roughness: 0.9, transparent: true, opacity: 0.6,
      });
      for (let vi = 0; vi < 2; vi++) {
        const veinGeo = new THREE.CylinderGeometry(0.003 * s, 0.002 * s, lowerArmLen * 0.6 * s, 3);
        const vein = new THREE.Mesh(veinGeo, veinMat);
        vein.position.set(
          (vi * 0.02 - 0.01) * s,
          -lowerArmLen * 0.4 * s,
          -armThickness * 0.7 * s
        );
        vein.rotation.x = 0.1;
        elbowPivot.add(vein);
      }

      bodyRoot.add(armPivot);
      return { pivot: armPivot, elbow: elbowPivot };
    };

    const leftArmResult = buildArm(-1);
    const rightArmResult = buildArm(1);
    this.leftArmPivot = leftArmResult.pivot;
    this.rightArmPivot = rightArmResult.pivot;
    this.leftElbow = leftArmResult.elbow;
    this.rightElbow = rightArmResult.elbow;

    // =====================
    // LEGS (upper + lower + foot)
    // =====================
    const buildLeg = (side: number) => {
      const legPivot = new THREE.Group();
      legPivot.position.set(side * hipWidth * 0.35 * s, 0, 0);

      // Crawler: legs drag behind
      if (id === 'crawler') {
        legPivot.rotation.x = 0.8 + Math.random() * 0.3;
      }

      // Upper leg
      const upperLegGeo = new THREE.CylinderGeometry(legThickness * s, legThickness * 0.85 * s, upperLegLen * s, 6);
      const upperLeg = new THREE.Mesh(upperLegGeo, clothMat);
      upperLeg.position.y = -upperLegLen * 0.5 * s;
      upperLeg.castShadow = true;
      legPivot.add(upperLeg);

      // Pants covering upper leg
      const pantLegGeo = new THREE.CylinderGeometry(legThickness * 1.2 * s, legThickness * 1.05 * s, upperLegLen * 0.95 * s, 6);
      const pantLeg = new THREE.Mesh(pantLegGeo, pantsMat);
      pantLeg.position.y = -upperLegLen * 0.45 * s;
      legPivot.add(pantLeg);
      // Torn bottom edge
      const pantTornGeo = new THREE.CylinderGeometry(legThickness * 1.08 * s, legThickness * 1.2 * s, 0.02 * s, 8, 1, true);
      const pantTorn = new THREE.Mesh(pantTornGeo, ragMat);
      pantTorn.position.y = -upperLegLen * 0.9 * s;
      legPivot.add(pantTorn);

      // Knee pivot
      const kneePivot = new THREE.Group();
      kneePivot.position.y = -upperLegLen * s;
      kneePivot.rotation.x = id === 'crawler' ? 0.5 : 0;
      legPivot.add(kneePivot);

      // Lower pants (shorter, some legs exposed)
      {
        const lowerPantGeo = new THREE.CylinderGeometry(legThickness * 1.0 * s, legThickness * 0.85 * s, lowerLegLen * 0.5 * s, 6);
        const lowerPant = new THREE.Mesh(lowerPantGeo, pantsMat);
        lowerPant.position.y = -lowerLegLen * 0.2 * s;
        kneePivot.add(lowerPant);
      }

      // Lower leg
      const lowerLegGeo = new THREE.CylinderGeometry(legThickness * 0.8 * s, legThickness * 0.6 * s, lowerLegLen * s, 6);
      const lowerLeg = new THREE.Mesh(lowerLegGeo, skinMat);
      lowerLeg.position.y = -lowerLegLen * 0.5 * s;
      lowerLeg.castShadow = true;
      kneePivot.add(lowerLeg);

      // Foot / Boot
      if (hasBoots) {
        // Military boot
        const bootGeo = new THREE.BoxGeometry(legThickness * 1.6 * s, 0.1 * s, 0.2 * s);
        const boot = new THREE.Mesh(bootGeo, leatherMat);
        boot.position.set(0, -lowerLegLen * s + 0.02 * s, -0.04 * s);
        boot.castShadow = true;
        kneePivot.add(boot);
        // Boot shaft
        const shaftGeo = new THREE.CylinderGeometry(legThickness * 0.85 * s, legThickness * 0.9 * s, 0.08 * s, 6);
        const shaft = new THREE.Mesh(shaftGeo, leatherMat);
        shaft.position.set(0, -lowerLegLen * s + 0.08 * s, -0.02 * s);
        kneePivot.add(shaft);
        // Boot sole
        const soleGeo = new THREE.BoxGeometry(legThickness * 1.6 * s, 0.02 * s, 0.22 * s);
        const sole = new THREE.Mesh(soleGeo, new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.95 }));
        sole.position.set(0, -lowerLegLen * s - 0.03 * s, -0.04 * s);
        kneePivot.add(sole);
      } else {
        // Bare foot / shoe
        const footGeo = new THREE.BoxGeometry(legThickness * 1.4 * s, 0.05 * s, 0.16 * s);
        const footMat = Math.random() > 0.5 ? skinMat : new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.8 }); // shoe or barefoot
        const foot = new THREE.Mesh(footGeo, footMat);
        foot.position.set(0, -lowerLegLen * s, -0.04 * s);
        foot.castShadow = true;
        kneePivot.add(foot);
      }

      // Toes
      for (let ti = -1; ti <= 1; ti++) {
        const toeGeo = new THREE.SphereGeometry(0.012 * s, 4, 3);
        const toe = new THREE.Mesh(toeGeo, skinMat);
        toe.position.set(ti * 0.02 * s, -lowerLegLen * s - 0.01 * s, -0.1 * s);
        kneePivot.add(toe);
      }

      // Kneecap
      const kneecapGeo = new THREE.SphereGeometry(legThickness * 0.7 * s, 5, 4);
      const kneecap = new THREE.Mesh(kneecapGeo, skinMat);
      kneecap.position.set(0, 0, -legThickness * 0.5 * s);
      kneecap.scale.set(0.8, 0.7, 0.5);
      kneePivot.add(kneecap);

      // Wound on leg (random)
      if (Math.random() > 0.6) {
        const legWoundGeo = new THREE.PlaneGeometry(0.05 * s, 0.035 * s);
        const legWoundMat = new THREE.MeshStandardMaterial({
          color: 0x8b1a1a, roughness: 0.6,
        });
        const legWound = new THREE.Mesh(legWoundGeo, legWoundMat);
        legWound.position.set(
          side * legThickness * 0.5 * s,
          -upperLegLen * 0.4 * s,
          0
        );
        legWound.rotation.y = side * 0.4;
        legPivot.add(legWound);
      }

      // Blood drip trail down leg
      if (Math.random() > 0.5) {
        const dripMat = new THREE.MeshStandardMaterial({
          color: 0x4a0000, roughness: 0.95, transparent: true, opacity: 0.7,
        });
        const dripGeo = new THREE.CylinderGeometry(0.005 * s, 0.008 * s, upperLegLen * 0.5 * s, 4);
        const drip = new THREE.Mesh(dripGeo, dripMat);
        drip.position.set(
          side * legThickness * 0.3 * s,
          -upperLegLen * 0.5 * s,
          -legThickness * 0.3 * s
        );
        legPivot.add(drip);
      }

      bodyRoot.add(legPivot);
      return { pivot: legPivot, knee: kneePivot };
    };

    const leftLegResult = buildLeg(-1);
    const rightLegResult = buildLeg(1);
    this.leftLegPivot = leftLegResult.pivot;
    this.rightLegPivot = rightLegResult.pivot;
    this.leftKnee = leftLegResult.knee;
    this.rightKnee = rightLegResult.knee;

    // =====================
    // TYPE-SPECIFIC EXTRA DETAILS
    // =====================
    if (id === 'spitter') {
      // Acid drool dripping from mouth
      const droolMat = new THREE.MeshStandardMaterial({
        color: 0x44cc44, emissive: 0x22aa22, emissiveIntensity: 0.4,
        transparent: true, opacity: 0.6, roughness: 0.3,
      });
      for (let di = 0; di < 3; di++) {
        const dLen = (0.05 + Math.random() * 0.08) * s;
        const droolGeo = new THREE.CylinderGeometry(0.004 * s, 0.002 * s, dLen, 3);
        const drool = new THREE.Mesh(droolGeo, droolMat);
        drool.position.set(
          (di - 1) * 0.02 * s,
          headCenterY - hipY - headRadius * 0.9 * s - dLen * 0.5,
          -headRadius * 0.6 * s
        );
        bodyRoot.add(drool);
      }
      // Bloated throat sac
      const sacGeo = new THREE.SphereGeometry(0.05 * s, 8, 6);
      const sacMat = new THREE.MeshStandardMaterial({
        color: 0x55aa55, emissive: 0x228822, emissiveIntensity: 0.3,
        transparent: true, opacity: 0.5, roughness: 0.4,
      });
      const sac = new THREE.Mesh(sacGeo, sacMat);
      sac.position.set(0, waistHeight * s + torsoHeight * s + neckHeight * s * 0.3, -0.07 * s);
      sac.scale.set(1.2, 1.0, 1.4);
      bodyRoot.add(sac);
    }

    if (id === 'exploder') {
      // Pulsing veins across body
      const veinGlowMat = new THREE.MeshStandardMaterial({
        color: 0xff4400, emissive: 0xff2200, emissiveIntensity: 0.8,
        roughness: 0.5,
      });
      const veinPaths = [
        { start: [0, waistHeight * s, 0], len: torsoHeight * 0.8 * s, angle: 0.2 },
        { start: [torsoWidthTop * 0.3 * s, waistHeight * s + torsoHeight * 0.3 * s, -torsoDepth * 0.3 * s], len: torsoHeight * 0.5 * s, angle: -0.3 },
        { start: [-torsoWidthTop * 0.25 * s, waistHeight * s + torsoHeight * 0.5 * s, torsoDepth * 0.2 * s], len: torsoHeight * 0.4 * s, angle: 0.4 },
      ];
      for (const vp of veinPaths) {
        const vGeo = new THREE.CylinderGeometry(0.006 * s, 0.004 * s, vp.len, 4);
        const v = new THREE.Mesh(vGeo, veinGlowMat);
        v.position.set(vp.start[0], vp.start[1] + vp.len * 0.5, vp.start[2]);
        v.rotation.z = vp.angle;
        bodyRoot.add(v);
      }
      // Glowing cracks in skin
      for (let ci = 0; ci < 5; ci++) {
        const crackGeo = new THREE.PlaneGeometry(0.005 * s, (0.04 + Math.random() * 0.06) * s);
        const crack = new THREE.Mesh(crackGeo, veinGlowMat);
        const cAngle = Math.random() * Math.PI * 2;
        crack.position.set(
          Math.sin(cAngle) * torsoWidthTop * 0.4 * s,
          waistHeight * s + Math.random() * torsoHeight * s,
          Math.cos(cAngle) * torsoDepth * 0.45 * s
        );
        crack.rotation.set(Math.random() * 0.5, cAngle, Math.random() * 0.5);
        bodyRoot.add(crack);
      }
    }

    if (id === 'tank') {
      // Chain/shackle on wrist
      const chainMat = new THREE.MeshStandardMaterial({
        color: 0x666666, roughness: 0.4, metalness: 0.8,
      });
      const shackleGeo = new THREE.TorusGeometry(armThickness * 1.2 * s, 0.008 * s, 6, 12);
      const shackle = new THREE.Mesh(shackleGeo, chainMat);
      shackle.position.set(-shoulderWidth * 0.5 * s, waistHeight * s + torsoHeight * s * 0.9 - upperArmLen * 0.8 * s, 0);
      shackle.rotation.x = Math.PI / 2;
      bodyRoot.add(shackle);
      // Hanging chain links
      for (let li = 0; li < 4; li++) {
        const linkGeo = new THREE.TorusGeometry(0.015 * s, 0.004 * s, 4, 8);
        const link = new THREE.Mesh(linkGeo, chainMat);
        link.position.set(
          -shoulderWidth * 0.5 * s - 0.02 * s,
          waistHeight * s + torsoHeight * s * 0.9 - upperArmLen * 0.8 * s - li * 0.035 * s,
          0
        );
        link.rotation.set(li % 2 === 0 ? 0 : Math.PI / 2, 0, 0);
        bodyRoot.add(link);
      }
      // Metal plate armor on chest
      const armorMat = new THREE.MeshStandardMaterial({
        color: 0x555555, roughness: 0.3, metalness: 0.9,
      });
      const plateGeo = new THREE.BoxGeometry(torsoWidthTop * 0.7 * s, torsoHeight * 0.5 * s, 0.02 * s);
      const plate = new THREE.Mesh(plateGeo, armorMat);
      plate.position.set(0, waistHeight * s + torsoHeight * s * 0.6, -torsoDepth * 0.52 * s);
      bodyRoot.add(plate);
    }

    if (id === 'crawler') {
      // Trail of blood behind
      const trailMat = new THREE.MeshStandardMaterial({
        color: 0x4a0000, roughness: 0.95, transparent: true, opacity: 0.5,
      });
      const trailGeo = new THREE.PlaneGeometry(0.15 * s, 0.6 * s);
      const trail = new THREE.Mesh(trailGeo, trailMat);
      trail.position.set(0, 0.005, 0.4 * s);
      trail.rotation.x = -Math.PI / 2;
      this.mesh.add(trail); // Attached to mesh, not bodyRoot
    }

    // =====================
    // BOSS: Unique crown, horns + glow per boss
    // =====================
    if (this.isBoss) {
      const bvl = this.bossVisual;
      const crownColorHex = bvl?.crownColor ?? 0xffd700;
      const auraColorHex = bvl?.auraColor ?? 0xff4400;
      const hornColorHex = bvl?.hornColor ?? 0x444444;
      const emissiveHex = bvl?.emissiveColor ?? 0xffd700;

      const crownGroup = new THREE.Group();
      crownGroup.position.y = headCenterY - hipY + headRadius * 1.1 * s;
      crownGroup.rotation.x = -hunchAngle * 0.6;

      const crownMat = new THREE.MeshStandardMaterial({
        color: crownColorHex,
        emissive: crownColorHex,
        emissiveIntensity: 0.8,
        roughness: 0.3,
        metalness: 0.7,
      });

      // Crown band
      const bandGeo = new THREE.TorusGeometry(headRadius * 0.85 * s, 0.02 * s, 6, 16);
      const band = new THREE.Mesh(bandGeo, crownMat);
      band.rotation.x = Math.PI / 2;
      crownGroup.add(band);

      // Crown spikes
      const hornCount = 5;
      for (let i = 0; i < hornCount; i++) {
        const angle = (i / hornCount) * Math.PI * 2;
        const hornH = (0.12 + Math.random() * 0.08) * s;
        const hornGeo = new THREE.ConeGeometry(0.025 * s, hornH, 5);
        const horn = new THREE.Mesh(hornGeo, crownMat);
        const r = headRadius * 0.85 * s;
        horn.position.set(Math.sin(angle) * r, hornH * 0.5, Math.cos(angle) * r);
        horn.rotation.x = Math.cos(angle) * 0.3;
        horn.rotation.z = -Math.sin(angle) * 0.3;
        crownGroup.add(horn);
      }

      // Two large demon horns with boss-specific color
      const demonHornMat = new THREE.MeshStandardMaterial({
        color: hornColorHex,
        roughness: 0.6,
        metalness: 0.4,
        emissive: emissiveHex,
        emissiveIntensity: 0.3,
      });
      for (const hSide of [-1, 1]) {
        const bigHornGeo = new THREE.ConeGeometry(0.04 * s, 0.25 * s, 6);
        const bigHorn = new THREE.Mesh(bigHornGeo, demonHornMat);
        bigHorn.position.set(hSide * headRadius * 0.7 * s, 0.08 * s, 0);
        bigHorn.rotation.z = -hSide * 0.4;
        bigHorn.rotation.x = -0.2;
        crownGroup.add(bigHorn);
      }

      // Glowing aura ring with boss-specific color
      const auraGeo = new THREE.RingGeometry(headRadius * 1.2 * s, headRadius * 1.4 * s, 24);
      const auraMat = new THREE.MeshBasicMaterial({
        color: auraColorHex,
        transparent: true,
        opacity: 0.35,
        side: THREE.DoubleSide,
      });
      const aura = new THREE.Mesh(auraGeo, auraMat);
      aura.rotation.x = Math.PI / 2;
      aura.position.y = -0.02 * s;
      crownGroup.add(aura);

      // Boss body emissive glow
      this.bodyMaterial.emissive.setHex(emissiveHex);
      this.bodyMaterial.emissiveIntensity = 0.15;

      // Weak point glowing marker (pulsating)
      if (this.bossWeakPoint) {
        const wpColor = this.bossWeakPoint.glowColor;
        const markerMat = new THREE.MeshStandardMaterial({
          color: wpColor, emissive: wpColor, emissiveIntensity: 2.0,
          transparent: true, opacity: 0.7,
        });
        this.weakPointMarkerMat = markerMat;
        let markerY = 0;
        let markerSize = 0.1 * s;
        switch (this.bossWeakPoint.zone) {
          case 'head': markerY = headCenterY - hipY; markerSize = 0.09 * s; break;
          case 'chest': markerY = chestTopY - hipY - torsoHeight * 0.25 * s; break;
          case 'belly': markerY = waistTopY - hipY + waistHeight * 0.3 * s; break;
          case 'back': markerY = chestTopY - hipY - torsoHeight * 0.3 * s; break;
          case 'legs': markerY = kneeY - hipY; markerSize = 0.08 * s; break;
        }
        const markerGeo = new THREE.SphereGeometry(markerSize, 8, 6);
        const marker = new THREE.Mesh(markerGeo, markerMat);
        marker.position.y = markerY;
        if (this.bossWeakPoint.zone === 'back') {
          marker.position.z = torsoDepth * 0.6 * s;
        } else {
          marker.position.z = -torsoDepth * 0.5 * s;
        }
        bodyRoot.add(marker);

        // Outer glow ring for extra visibility
        const ringGeo = new THREE.RingGeometry(markerSize * 0.8, markerSize * 1.2, 12);
        const ringMat = new THREE.MeshBasicMaterial({
          color: wpColor, transparent: true, opacity: 0.4, side: THREE.DoubleSide,
        });
        this.weakPointRingMat = ringMat;
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.position.copy(marker.position);
        bodyRoot.add(ring);

        // Point light at weak point (skip on mobile)
        const isMob = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        if (!isMob) {
          const wpLight = new THREE.PointLight(wpColor, 1.5, 2);
          wpLight.position.copy(marker.position);
          bodyRoot.add(wpLight);
        }
      }

      bodyRoot.add(crownGroup);
    }

    // HP bar (billboard)
    const hpBarY = (this.isBoss ? totalHeight + 0.3 * s : totalHeight + 0.15 * s);
    this.createHPBar(hpBarY);
  }

  private hpBarFill: THREE.Mesh | null = null;
  private hpBarGroup: THREE.Group | null = null;

  private createHPBar(yPos: number): void {
    const barWidth = this.isBoss ? 1.0 : 0.6;
    const barHeight = this.isBoss ? 0.09 : 0.06;

    this.hpBarGroup = new THREE.Group();
    this.hpBarGroup.position.y = yPos;

    // Background
    const bgGeom = new THREE.PlaneGeometry(barWidth, barHeight);
    const bgMat = new THREE.MeshBasicMaterial({ color: 0x333333, transparent: true, opacity: 0.8 });
    const bg = new THREE.Mesh(bgGeom, bgMat);
    this.hpBarGroup.add(bg);

    // Fill
    const fillGeom = new THREE.PlaneGeometry(barWidth - 0.02, barHeight - 0.02);
    const fillMat = new THREE.MeshBasicMaterial({ color: this.isBoss ? 0xff4444 : 0x44ff44 });
    this.hpBarFill = new THREE.Mesh(fillGeom, fillMat);
    this.hpBarFill.position.z = 0.001;
    this.hpBarGroup.add(this.hpBarFill);

    // Boss name label background
    if (this.isBoss) {
      const nameBgGeo = new THREE.PlaneGeometry(barWidth, barHeight * 0.7);
      const nameBgMat = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.5 });
      const nameBg = new THREE.Mesh(nameBgGeo, nameBgMat);
      nameBg.position.y = barHeight * 0.9;
      this.hpBarGroup.add(nameBg);
    }

    this.mesh.add(this.hpBarGroup);
  }

  takeDamage(amount: number): boolean {
    // Shield reduces damage by 70%
    const finalDmg = this.shieldActive ? Math.round(amount * 0.3) : amount;
    this.hp -= finalDmg;
    this.flashTimer = 0.1;

    // Update HP bar
    if (this.hpBarFill) {
      const ratio = Math.max(0, this.hp / this.maxHp);
      const halfFillWidth = (this.isBoss ? 0.98 : 0.58) / 2;
      this.hpBarFill.scale.x = ratio;
      this.hpBarFill.position.x = -(1 - ratio) * halfFillWidth / 2;

      const mat = this.hpBarFill.material as THREE.MeshBasicMaterial;
      if (this.isBoss) {
        // Boss: red gradient
        if (ratio > 0.5) mat.color.setHex(0xff4444);
        else if (ratio > 0.25) mat.color.setHex(0xff2200);
        else mat.color.setHex(0xcc0000);
      } else {
        if (ratio > 0.5) mat.color.setHex(0x44ff44);
        else if (ratio > 0.25) mat.color.setHex(0xffaa00);
        else mat.color.setHex(0xff4444);
      }
    }

    if (this.hp <= 0) {
      this.state = ZombieState.DEAD;
      this.onDeath();
      return true;
    }

    if (this.state === ZombieState.WANDER || this.state === ZombieState.IDLE) {
      this.state = ZombieState.ALERT;
      this.alertTimer = 0.5;
    }
    return false;
  }

  private onDeath(): void {
    // Death animation: fall over
    const tween = { progress: 0 };
    const animate = () => {
      tween.progress += 0.05;
      this.mesh.rotation.x = (Math.PI / 2) * Math.min(tween.progress, 1);
      this.mesh.position.y = -0.5 * Math.min(tween.progress, 1);
      if (tween.progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Fade out
        setTimeout(() => {
          this.mesh.visible = false;
        }, 2000);
      }
    };
    animate();
  }

  isDead(): boolean {
    return this.state === ZombieState.DEAD;
  }

  update(dt: number, playerPosition: THREE.Vector3, colliders: THREE.Box3[], camera?: THREE.Camera): void {
    if (this.isDead()) return;

    // Billboard HP bar toward camera (simple Y-axis rotation only)
    if (this.hpBarGroup && camera) {
      const dx = camera.position.x - this.position.x;
      const dz = camera.position.z - this.position.z;
      this.hpBarGroup.rotation.y = Math.atan2(dx, dz);
    }

    // Use XZ distance only (ignore height difference)
    const dx = this.position.x - playerPosition.x;
    const dz = this.position.z - playerPosition.z;
    const distToPlayer = Math.sqrt(dx * dx + dz * dz);

    // Damage flash
    if (this.flashTimer > 0) {
      this.flashTimer -= dt;
      this.bodyMaterial.emissive.setHex(0xff0000);
      this.bodyMaterial.emissiveIntensity = 1;
    } else {
      this.bodyMaterial.emissive.setHex(0x000000);
      this.bodyMaterial.emissiveIntensity = 0;
    }

    // State machine
    switch (this.state) {
      case ZombieState.IDLE:
      case ZombieState.WANDER:
        this.updateWander(dt);
        if (distToPlayer < this.config.detectionRange) {
          this.state = ZombieState.CHASE;
        }
        break;

      case ZombieState.ALERT:
        this.alertTimer -= dt;
        if (this.alertTimer <= 0) {
          this.state = ZombieState.CHASE;
        }
        break;

      case ZombieState.CHASE:
        this.updateChase(dt, playerPosition, colliders);
        if (distToPlayer < this.config.attackRange) {
          this.state = ZombieState.ATTACK;
        }
        if (distToPlayer > this.config.detectionRange * 2) {
          this.state = ZombieState.WANDER;
        }
        break;

      case ZombieState.ATTACK:
        // Keep moving toward player while attacking
        this.updateChase(dt, playerPosition, colliders);
        this.updateAttack(dt);
        if (distToPlayer > this.config.attackRange * 1.5) {
          this.state = ZombieState.CHASE;
        }
        break;
    }

    // Boss abilities (runs during chase and attack)
    if (this.isBoss && (this.state === ZombieState.CHASE || this.state === ZombieState.ATTACK)) {
      this.updateBossAbilities(dt, playerPosition);
    }

    // Pulse weak point marker (skip ring update on mobile)
    if (this.isBoss && this.weakPointMarkerMat) {
      const pulse = Math.sin(performance.now() * 0.005) * 0.5 + 0.5;
      this.weakPointMarkerMat.emissiveIntensity = 1.5 + pulse * 2.0;
    }

    // Animate the zombie
    this.animateBody(dt);

    this.mesh.position.copy(this.position);
  }

  private animateBody(dt: number): void {
    const id = this.config.id;
    const isMoving = this.state === ZombieState.CHASE || this.state === ZombieState.WANDER || this.state === ZombieState.ATTACK;
    const speedFactor = this.state === ZombieState.CHASE ? 1.0 : (this.state === ZombieState.WANDER ? 0.4 : 0.2);

    this.animPhase += dt * this.animSpeed * speedFactor * 6.0;
    const t = this.animPhase;
    const sin = Math.sin(t);
    const cos = Math.cos(t);

    if (!isMoving) {
      // Idle sway - subtle breathing and head movement
      if (this.bodyRoot) {
        this.bodyRoot.rotation.x = this.baseHunch + Math.sin(t * 0.3) * 0.02;
      }
      if (this.headGroup) {
        this.headGroup.rotation.z = Math.sin(t * 0.2) * 0.05;
        this.headGroup.rotation.y = Math.sin(t * 0.15) * 0.08;
      }
      return;
    }

    // --- ATTACK animation (overrides walk when attacking) ---
    // Note: model faces -Z, but mesh is rotated PI via faceToward()
    // So arm rotation.x positive = swing forward (toward player)
    if (this.state === ZombieState.ATTACK) {
      const attackT = t * 3.0;
      const attackSin = Math.sin(attackT);
      const attackCos = Math.cos(attackT);

      // Legs planted
      if (this.leftLegPivot) this.leftLegPivot.rotation.x = 0.1;
      if (this.rightLegPivot) this.rightLegPivot.rotation.x = -0.1;
      if (this.leftKnee) this.leftKnee.rotation.x = 0.15;
      if (this.rightKnee) this.rightKnee.rotation.x = 0.15;

      if (id === 'crawler') {
        if (this.leftArmPivot) {
          this.leftArmPivot.rotation.x = 1.5 + attackSin * 0.8;
          this.leftArmPivot.rotation.z = -0.3;
        }
        if (this.rightArmPivot) {
          this.rightArmPivot.rotation.x = 1.5 - attackSin * 0.8;
          this.rightArmPivot.rotation.z = 0.3;
        }
      } else if (id === 'tank') {
        // Heavy overhead smash - raise up then slam
        const smashPhase = (attackSin + 1) * 0.5;
        const armAngle = 0.5 + smashPhase * 2.0;
        if (this.leftArmPivot) {
          this.leftArmPivot.rotation.x = armAngle;
          this.leftArmPivot.rotation.z = -0.3;
        }
        if (this.rightArmPivot) {
          this.rightArmPivot.rotation.x = armAngle;
          this.rightArmPivot.rotation.z = 0.3;
        }
        if (this.leftElbow) this.leftElbow.rotation.x = 0.3;
        if (this.rightElbow) this.rightElbow.rotation.x = 0.3;
        if (this.bodyRoot) {
          this.bodyRoot.rotation.x = this.baseHunch - smashPhase * 0.3;
        }
      } else if (id === 'spitter') {
        if (this.headGroup) {
          this.headGroup.rotation.x = -this.baseHunch * 0.6 + 0.3 + attackSin * 0.4;
        }
        if (this.bodyRoot) {
          this.bodyRoot.rotation.x = this.baseHunch - 0.2 + attackSin * 0.15;
        }
        if (this.leftArmPivot) this.leftArmPivot.rotation.z = -0.6;
        if (this.rightArmPivot) this.rightArmPivot.rotation.z = 0.6;
      } else if (id === 'exploder') {
        const tremble = Math.sin(t * 15) * 0.05;
        if (this.bodyRoot) {
          this.bodyRoot.rotation.x = this.baseHunch + tremble;
          this.bodyRoot.rotation.z = tremble * 1.5;
        }
        if (this.leftArmPivot) this.leftArmPivot.rotation.z = -0.8 + tremble;
        if (this.rightArmPivot) this.rightArmPivot.rotation.z = 0.8 + tremble;
      } else {
        // Normal zombie: alternating arm swipes toward player
        const swipeArm = attackSin > 0 ? 'left' : 'right';
        if (this.leftArmPivot) {
          if (swipeArm === 'left') {
            this.leftArmPivot.rotation.x = 1.8 + attackSin * 1.0;
            this.leftArmPivot.rotation.z = -0.3 + attackCos * 0.2;
          } else {
            this.leftArmPivot.rotation.x = -this.baseArmAngle;
          }
        }
        if (this.rightArmPivot) {
          if (swipeArm === 'right') {
            this.rightArmPivot.rotation.x = 1.8 - attackSin * 1.0;
            this.rightArmPivot.rotation.z = 0.3 - attackCos * 0.2;
          } else {
            this.rightArmPivot.rotation.x = -this.baseArmAngle;
          }
        }
        if (this.leftElbow) this.leftElbow.rotation.x = 0.6;
        if (this.rightElbow) this.rightElbow.rotation.x = 0.6;
        if (this.bodyRoot) {
          this.bodyRoot.rotation.x = this.baseHunch - 0.15 - Math.abs(attackSin) * 0.1;
        }
        if (this.headGroup) {
          this.headGroup.rotation.x = -this.baseHunch * 0.6 + 0.15;
          this.headGroup.rotation.z = attackSin * 0.1;
        }
      }

      if (id !== 'crawler') {
        this.mesh.position.y = Math.abs(attackSin) * 0.02;
      }
      return;
    }

    // --- Walking/running animation per type ---

    if (id === 'crawler') {
      // Crawling: arms pull body forward alternately
      if (this.leftArmPivot) {
        this.leftArmPivot.rotation.x = this.baseArmAngle + sin * 0.6;
      }
      if (this.rightArmPivot) {
        this.rightArmPivot.rotation.x = this.baseArmAngle - sin * 0.6;
      }
      if (this.leftElbow) this.leftElbow.rotation.x = -0.8 + cos * 0.3;
      if (this.rightElbow) this.rightElbow.rotation.x = -0.8 - cos * 0.3;
      // Body undulates (use absolute value, not additive)
      if (this.bodyRoot) {
        this.bodyRoot.rotation.x = this.baseHunch + sin * 0.1;
      }
      return;
    }

    if (id === 'tank') {
      // Heavy stomping walk - slow, deliberate
      const legSwing = sin * 0.3;
      if (this.leftLegPivot) this.leftLegPivot.rotation.x = legSwing;
      if (this.rightLegPivot) this.rightLegPivot.rotation.x = -legSwing;
      if (this.leftKnee) this.leftKnee.rotation.x = Math.max(0, -cos) * 0.3;
      if (this.rightKnee) this.rightKnee.rotation.x = Math.max(0, cos) * 0.3;
      // Arms swing minimally, fists clenched
      if (this.leftArmPivot) this.leftArmPivot.rotation.x = this.baseArmAngle - sin * 0.15;
      if (this.rightArmPivot) this.rightArmPivot.rotation.x = this.baseArmAngle + sin * 0.15;
      // Heavy shoulder roll
      if (this.bodyRoot) {
        this.bodyRoot.rotation.z = sin * 0.05;
        this.bodyRoot.rotation.x = this.baseHunch;
      }
      // Stomp shake
      this.mesh.position.y = Math.abs(Math.sin(t * 2)) * 0.02;
      return;
    }

    if (id === 'spitter') {
      // Lurching walk, head bobbing, arms out to sides
      const legSwing = sin * 0.35;
      if (this.leftLegPivot) this.leftLegPivot.rotation.x = legSwing;
      if (this.rightLegPivot) this.rightLegPivot.rotation.x = -legSwing;
      if (this.leftKnee) this.leftKnee.rotation.x = Math.max(0, -cos) * 0.4;
      if (this.rightKnee) this.rightKnee.rotation.x = Math.max(0, cos) * 0.4;
      // Arms held wide, swaying
      if (this.leftArmPivot) {
        this.leftArmPivot.rotation.x = this.baseArmAngle * 0.5;
        this.leftArmPivot.rotation.z = -0.4 + sin * 0.15;
      }
      if (this.rightArmPivot) {
        this.rightArmPivot.rotation.x = this.baseArmAngle * 0.5;
        this.rightArmPivot.rotation.z = 0.4 - sin * 0.15;
      }
      // Head bobs forward (about to spit)
      if (this.headGroup) {
        this.headGroup.rotation.x = -this.baseHunch * 0.6 + sin * 0.1;
      }
      if (this.bodyRoot) {
        this.bodyRoot.rotation.x = this.baseHunch + Math.abs(sin) * 0.05;
      }
      this.mesh.position.y = Math.abs(sin) * 0.03;
      return;
    }

    if (id === 'exploder') {
      // Stumbling, unstable walk - lurching side to side
      const legSwing = sin * 0.4;
      if (this.leftLegPivot) this.leftLegPivot.rotation.x = legSwing;
      if (this.rightLegPivot) this.rightLegPivot.rotation.x = -legSwing;
      if (this.leftKnee) this.leftKnee.rotation.x = Math.max(0, -cos) * 0.5;
      if (this.rightKnee) this.rightKnee.rotation.x = Math.max(0, cos) * 0.5;
      // Arms out wide, twitching
      if (this.leftArmPivot) {
        this.leftArmPivot.rotation.x = this.baseArmAngle * 0.3;
        this.leftArmPivot.rotation.z = -0.6 + Math.sin(t * 3) * 0.1;
      }
      if (this.rightArmPivot) {
        this.rightArmPivot.rotation.x = this.baseArmAngle * 0.3;
        this.rightArmPivot.rotation.z = 0.6 + Math.sin(t * 3.5) * 0.1;
      }
      // Heavy body sway (about to explode)
      if (this.bodyRoot) {
        this.bodyRoot.rotation.z = sin * 0.12;
        this.bodyRoot.rotation.x = this.baseHunch + Math.sin(t * 2) * 0.06;
      }
      this.mesh.position.y = Math.abs(sin) * 0.04;
      return;
    }

    // --- Normal zombie: shambling, limping, dragging ---
    // Asymmetric gait - one leg drags, giving a limp
    const slowSin = Math.sin(t * 0.8); // slower cycle for shamble
    const slowCos = Math.cos(t * 0.8);
    const legSwing = slowSin * 0.35;

    // Left leg moves normally, right leg drags (limp)
    if (this.leftLegPivot) this.leftLegPivot.rotation.x = legSwing;
    if (this.rightLegPivot) this.rightLegPivot.rotation.x = -legSwing * 0.5; // right leg barely moves

    // Left knee bends normally, right knee stiff
    if (this.leftKnee) this.leftKnee.rotation.x = Math.max(0, -slowCos) * 0.5;
    if (this.rightKnee) this.rightKnee.rotation.x = Math.max(0, slowCos) * 0.15; // stiff knee

    // Arms: one reaches forward (classic zombie), other hangs limp
    if (this.leftArmPivot) {
      // Reaching forward arm
      this.leftArmPivot.rotation.x = this.baseArmAngle + Math.sin(t * 0.4) * 0.15;
      this.leftArmPivot.rotation.z = -0.1;
    }
    if (this.rightArmPivot) {
      // Limp dangling arm, swinging with body
      this.rightArmPivot.rotation.x = -0.1 + slowSin * 0.2;
      this.rightArmPivot.rotation.z = 0.15 + Math.sin(t * 0.6) * 0.06;
    }

    // Elbows: reaching arm bent, limp arm straight
    if (this.leftElbow) this.leftElbow.rotation.x = -0.6 + Math.sin(t * 0.5) * 0.1;
    if (this.rightElbow) this.rightElbow.rotation.x = -0.1 + Math.sin(t * 0.3) * 0.05;

    // Body tilts toward the bad leg, slow sway
    if (this.bodyRoot) {
      this.bodyRoot.rotation.z = 0.05 + slowSin * 0.08; // lean to one side
      this.bodyRoot.rotation.x = this.baseHunch + Math.abs(slowSin) * 0.04;
      this.bodyRoot.rotation.y = Math.sin(t * 0.3) * 0.04; // slight twist
    }

    // Head lolls to opposite side of body lean, slow random movement
    if (this.headGroup) {
      this.headGroup.rotation.z = -0.08 + slowSin * 0.12;
      this.headGroup.rotation.y = Math.sin(t * 0.25) * 0.1;
      this.headGroup.rotation.x = -this.baseHunch * 0.6 + Math.sin(t * 0.35) * 0.06;
    }

    // Uneven gait bounce
    this.mesh.position.y = Math.abs(sin) * 0.03;
  }

  private updateWander(dt: number): void {
    this.wanderTimer -= dt;
    if (this.wanderTimer <= 0 || !this.wanderTarget) {
      this.wanderTarget = new THREE.Vector3(
        this.position.x + (Math.random() - 0.5) * 10,
        0,
        this.position.z + (Math.random() - 0.5) * 10
      );
      this.wanderTimer = 3 + Math.random() * 4;
    }

    const dir = this.wanderTarget.clone().sub(this.position);
    dir.y = 0;
    dir.normalize();

    this.position.x += dir.x * this.config.speed * 0.3 * dt;
    this.position.z += dir.z * this.config.speed * 0.3 * dt;

    // Face movement direction
    this.faceToward(this.wanderTarget.x, this.wanderTarget.z);
  }

  private updateChase(dt: number, target: THREE.Vector3, colliders: THREE.Box3[]): void {
    const dir = new THREE.Vector3(target.x - this.position.x, 0, target.z - this.position.z);
    dir.normalize();

    const newX = this.position.x + dir.x * this.config.speed * dt;
    const newZ = this.position.z + dir.z * this.config.speed * dt;

    // Simple collision avoidance
    const zombieBox = new THREE.Box3(
      new THREE.Vector3(newX - 0.3, 0, newZ - 0.3),
      new THREE.Vector3(newX + 0.3, 1.8, newZ + 0.3)
    );

    let blocked = false;
    for (const collider of colliders) {
      if (zombieBox.intersectsBox(collider)) {
        blocked = true;
        break;
      }
    }

    if (!blocked) {
      this.position.x = newX;
      this.position.z = newZ;
    } else {
      // Try to flank around obstacle
      const perpDir = new THREE.Vector3(-dir.z, 0, dir.x);
      this.position.x += perpDir.x * this.config.speed * dt;
      this.position.z += perpDir.z * this.config.speed * dt;
    }

    this.faceToward(target.x, target.z);

    // Bobbing animation
    this.mesh.position.y = Math.sin(performance.now() * 0.005 * this.config.speed) * 0.05;
  }

  private faceToward(targetX: number, targetZ: number): void {
    const dx = targetX - this.position.x;
    const dz = targetZ - this.position.z;
    // Face is built at -Z direction, so add PI to face that direction toward target
    this.mesh.rotation.y = Math.atan2(dx, dz) + Math.PI;
  }

  private updateAttack(dt: number): void {
    this.attackCooldown -= dt;
  }

  // Called each frame from update() for boss ability logic
  private updateBossAbilities(dt: number, playerPosition: THREE.Vector3): void {
    if (!this.isBoss || this.bossAbilities.length === 0) return;

    // Shield timer
    if (this.shieldActive) {
      this.shieldTimer -= dt;
      if (this.shieldTimer <= 0) {
        this.shieldActive = false;
        this.mesh.traverse(c => {
          if (c instanceof THREE.Mesh && c.material instanceof THREE.MeshStandardMaterial) {
            c.material.transparent = false;
            c.material.opacity = 1;
          }
        });
      }
    }

    // Invisibility timer
    if (this.isInvisible) {
      this.invisibleTimer -= dt;
      if (this.invisibleTimer <= 0) {
        this.isInvisible = false;
        this.mesh.traverse(c => {
          if (c instanceof THREE.Mesh && c.material instanceof THREE.Material) {
            c.material.transparent = false;
            c.material.opacity = 1;
          }
        });
      }
    }

    // Charge movement
    if (this.chargeTarget) {
      const dx = this.chargeTarget.x - this.position.x;
      const dz = this.chargeTarget.z - this.position.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist < 0.5) {
        this.chargeTarget = null;
      } else {
        const step = this.chargeSpeed * dt;
        this.position.x += (dx / dist) * step;
        this.position.z += (dz / dist) * step;
      }
    }

    // Ability cooldown
    this.abilityCooldown -= dt;
    if (this.abilityCooldown > 0) return;

    // Use next ability
    const ability = this.bossAbilities[this.abilityIndex % this.bossAbilities.length];
    this.abilityIndex++;
    this.abilityCooldown = this.bossAbilityCooldownTime;
    this.pendingAbilityEffects = [];

    this.executeBossAbility(ability, playerPosition);
  }

  private executeBossAbility(ability: string, playerPosition: THREE.Vector3): void {
    const bv = this.bossVisual;
    const emColor = bv?.emissiveColor ?? 0xff4400;

    switch (ability) {
      case 'summon':
      case 'spawnMinions': {
        // Spawn 2-3 normal zombies nearby
        this.pendingAbilityEffects.push({
          type: 'spawn',
          spawnCount: ability === 'spawnMinions' ? 3 : 2,
          position: this.position.clone(),
        });
        this.flashAbilityVisual(emColor);
        break;
      }
      case 'syringeAttack': {
        // Long range stab - extra damage
        this.pendingAbilityEffects.push({
          type: 'damage',
          damage: Math.round(this.config.damage * 2),
        });
        this.flashAbilityVisual(0x44ff44);
        break;
      }
      case 'buffScream': {
        // Speed boost all nearby zombies
        this.pendingAbilityEffects.push({
          type: 'buff',
          speedMultiplier: 1.5,
          radius: 15,
          position: this.position.clone(),
        });
        this.flashAbilityVisual(emColor);
        break;
      }
      case 'frontArmor': {
        // Activate shield - reduce incoming damage
        this.shieldActive = true;
        this.shieldTimer = 5;
        this.pendingAbilityEffects.push({ type: 'shield', shieldActive: true });
        // Visual: make body slightly transparent/metallic
        this.mesh.traverse(c => {
          if (c instanceof THREE.Mesh && c.material instanceof THREE.MeshStandardMaterial) {
            c.material.transparent = true;
            c.material.opacity = 0.7;
            c.material.metalness = 0.9;
          }
        });
        break;
      }
      case 'charge': {
        // Rush toward player at high speed
        this.chargeTarget = playerPosition.clone();
        this.chargeSpeed = this.config.speed * 4;
        this.pendingAbilityEffects.push({
          type: 'damage',
          damage: Math.round(this.config.damage * 2.5),
        });
        this.flashAbilityVisual(0xff6600);
        break;
      }
      case 'poisonCloud': {
        // Area damage around self
        this.pendingAbilityEffects.push({
          type: 'areaDamage',
          damage: Math.round(this.config.damage * 0.5),
          radius: 8,
          position: this.position.clone(),
        });
        this.flashAbilityVisual(0x66ff00);
        break;
      }
      case 'acidSpit': {
        // Long range damage
        this.pendingAbilityEffects.push({
          type: 'damage',
          damage: Math.round(this.config.damage * 1.5),
        });
        this.flashAbilityVisual(0x44ff00);
        break;
      }
      case 'laserAttack': {
        // High damage ranged attack
        this.pendingAbilityEffects.push({
          type: 'damage',
          damage: Math.round(this.config.damage * 2),
        });
        this.flashAbilityVisual(0x00ccff);
        break;
      }
      case 'shield': {
        this.shieldActive = true;
        this.shieldTimer = 6;
        this.pendingAbilityEffects.push({ type: 'shield', shieldActive: true });
        this.mesh.traverse(c => {
          if (c instanceof THREE.Mesh && c.material instanceof THREE.MeshStandardMaterial) {
            c.material.transparent = true;
            c.material.opacity = 0.6;
            c.material.metalness = 1.0;
          }
        });
        break;
      }
      case 'teleport': {
        // Teleport near player
        const angle = Math.random() * Math.PI * 2;
        const dist = 3 + Math.random() * 2;
        this.position.x = playerPosition.x + Math.cos(angle) * dist;
        this.position.z = playerPosition.z + Math.sin(angle) * dist;
        this.flashAbilityVisual(emColor);
        break;
      }
      case 'commandZombies': {
        // Buff all zombies speed
        this.pendingAbilityEffects.push({
          type: 'buff',
          speedMultiplier: 1.8,
          radius: 30,
          position: this.position.clone(),
        });
        this.flashAbilityVisual(emColor);
        break;
      }
      case 'shockwave':
      case 'groundSlam': {
        // Area damage
        const mult = ability === 'groundSlam' ? 1.5 : 1.0;
        this.pendingAbilityEffects.push({
          type: 'areaDamage',
          damage: Math.round(this.config.damage * mult),
          radius: ability === 'groundSlam' ? 5 : 8,
          position: this.position.clone(),
        });
        this.flashAbilityVisual(0xff4400);
        break;
      }
      case 'invisibility': {
        this.isInvisible = true;
        this.invisibleTimer = 4;
        this.mesh.traverse(c => {
          if (c instanceof THREE.Mesh && c.material instanceof THREE.Material) {
            c.material.transparent = true;
            c.material.opacity = 0.15;
          }
        });
        break;
      }
      case 'backstab': {
        // Teleport behind player + heavy damage
        const dir = new THREE.Vector3().subVectors(this.position, playerPosition).normalize();
        this.position.x = playerPosition.x - dir.x * 2;
        this.position.z = playerPosition.z - dir.z * 2;
        this.pendingAbilityEffects.push({
          type: 'damage',
          damage: Math.round(this.config.damage * 3),
        });
        this.flashAbilityVisual(0x8800aa);
        break;
      }
      case 'gunAttack': {
        // Ranged burst
        this.pendingAbilityEffects.push({
          type: 'damage',
          damage: Math.round(this.config.damage * 1.8),
        });
        this.flashAbilityVisual(0xffcc00);
        break;
      }
      case 'commandSquad': {
        // Spawn + speed buff
        this.pendingAbilityEffects.push({
          type: 'spawn',
          spawnCount: 2,
          position: this.position.clone(),
        });
        this.pendingAbilityEffects.push({
          type: 'buff',
          speedMultiplier: 1.3,
          radius: 20,
          position: this.position.clone(),
        });
        this.flashAbilityVisual(emColor);
        break;
      }
      case 'randomBossAbility': {
        // Pick a random ability from all bosses
        const allAbilities = [
          'summon', 'syringeAttack', 'spawnMinions', 'buffScream',
          'charge', 'poisonCloud', 'laserAttack', 'shield',
          'teleport', 'shockwave', 'groundSlam', 'invisibility', 'backstab',
        ];
        const pick = allAbilities[Math.floor(Math.random() * allAbilities.length)];
        this.executeBossAbility(pick, playerPosition);
        break;
      }
    }
  }

  private flashAbilityVisual(color: number): void {
    this.bodyMaterial.emissive.setHex(color);
    this.bodyMaterial.emissiveIntensity = 2.0;
    // Flash will be reset by the normal flashTimer logic in update
    this.flashTimer = 0.3;
  }

  canAttack(): boolean {
    return this.state === ZombieState.ATTACK && this.attackCooldown <= 0;
  }

  performAttack(): number {
    this.attackCooldown = this.isBoss ? 1.0 : 1.5; // Bosses attack faster
    return this.config.damage;
  }

  dispose(): void {
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
}
